import { EReportTargetOperatorSymbolMap } from './reportConfiguration';

export const OG_META_DATA_KEY = 'OG_META_OG';
export const OG_CHILD_COUNT_COLUMN_NAME = '__og_child_count';
export const V3_COA_ACCOUNT_NUMBER_ID_COLUMN_NAME = 'account_number_id';
export const V2_COA_ACCOUNT_NUMBER_COLUMN_NAME = 'account_number';
export const COA_ACCOUNT_TYPE_COLUMN_NAME = 'account_type';
export const V2_COA_OG_ACCOUNT_NUMBER_COLUMN_NAME = '__og_account_number';
export const OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME = '__og_fiscal_year_fiscal_period';
export const NO_DATA_ROW_ID = 'no_data_row';
export const MANAGED_APPLICATION_COA = 'urn:opengov:application:chart-of-accounts';
export const MANAGED_APPLICATION_BUDGET = 'urn:opengov:application:budget';

export enum LOGICAL_TYPES {
  TEXT = 'urn:opengov:types/logical/text',
  DATE = 'urn:opengov:types/logical/date',
  NUMERIC = 'urn:opengov:types/logical/numeric?precision=4&scale=0',
  TIMESTAMP = 'urn:opengov:types/logical/timestamp',
  FISCAL_YEAR_FISCAL_PERIOD = 'urn:opengov:types/logical/fiscalYearFiscalPeriod'
}

export enum SEMANTIC_TYPES {
  FISCAL_YEAR = 'urn:opengov:types/semantic/calendar/fiscal/year',
  PERCENTAGE = 'urn:opengov:types/semantic/numeric/percentage',
  COA_ACCOUNT_STATUS = 'urn:opengov:types/semantic/accounting/coa/account-status'
}

/**
 * Prefix shared by every calendar semantic type (fiscal year, calendar quarter, period, …).
 *
 * Lives here because both sides of the wire branch on it and must agree: the app excludes these
 * columns from the non-CoA picker so they stay under Year, and the service excludes them when
 * deciding whether a saved bare lens reference is a text or a date column dimension. Declaring it
 * twice would let the two drift and silently disagree about the same column.
 */
export const CALENDAR_SEMANTIC_PREFIX = 'urn:opengov:types/semantic/calendar';

export const EMPTY_VALUES = 'Empty Values';
export const TOTALS_COLUMN_LABEL = 'Totals';
/**
 * Suffix on the label of a row that aggregates its own subtree — "<row name> Totals".
 *
 * Shared because both sides of the boundary build these labels: reporting-service appends it in the
 * rows pipeline from the chart-of-accounts name, and reporting-app rebuilds it for rows whose name
 * comes from report config instead. Two literals would drift, and the mismatch would only surface
 * once a renamed row sat next to an un-renamed one.
 *
 * Deliberately separate from {@link TOTALS_COLUMN_LABEL} despite the identical value: renaming the
 * totals *column* header should not silently reword every totals *row*.
 */
export const TOTALS_ROW_LABEL_SUFFIX = 'Totals';
/**
 * The backend sends Totals columns with a null child value: [cgId, null, 'Totals'].
 * When stored in columnValueMapping (which uses string keys), null becomes this key.
 * Use this constant when reading/writing Totals entries in the mapping dictionary.
 */
export const TOTALS_COLUMN_VALUE_KEY = 'null';
export const CALCULATION_COLUMN_GROUP_HEADER = 'Calculation';

export const TARGET_CONDITIONS_OPERATORS: EReportTargetOperatorSymbolMap = {
  greaterThanOrEqualTo: '≥',
  lessThanOrEqualTo: '≤',
  lessThan: '<',
  greaterThan: '>',
  equalTo: '=',
  notEqualTo: '≠',
  between: '⇔'
};

export const ACCOUNT_SUMMARY_VISUALIZATION_FORMATTING = {
  currencyRounding: 0,
  numberRounding: 0,
  percentRounding: -2,
  negativeNumberDisplay: 0,
  zeroNumbers: [
    {
      label: 'rows',
      enabled: false
    }
  ]
};

export const CHARTS_AND_DETAILS_VISUALIZATION_FORMATTING = {
  currencyRounding: -4,
  numberRounding: -4,
  percentRounding: -2,
  negativeNumberDisplay: 0,
  zeroNumbers: []
};

export const LOCATION_MEASURE_COLUMN = 'accountSummary.measureColumn';
export const LOCATION_DATE_FILTER = 'accountSummary.dateFilter';
export const LOCATION_CALCULATED_COLUMNS = 'accountSummary.calculatedColumns';
export const LOCATION_ROW_FILTERS_GROUPS = 'accountSummary.rowFilters.group';
export const LOCATION_ROW_FILTERS_SEGMENTS = 'accountSummary.rowFilters.segment';
export const LOCATION_COLUMN_FILTERS_GROUPS = 'accountSummary.columnFilters.group';
export const LOCATION_COLUMN_FILTERS_SEGMENTS = 'accountSummary.columnFilters.segment';
/** Non-CoA (lens column) filters on a multi-hierarchy column group. */
export const LOCATION_COLUMN_FILTERS_COLUMNS = 'accountSummary.columnFilters.column';
export const LOCATION_VIEW_FILTERS_GROUPS = 'accountSummary.viewFilters.group';
export const LOCATION_VIEW_FILTERS_SEGMENTS = 'accountSummary.viewFilters.segment';
export const LOCATION_COLUMN_GROUPING_GROUPS = 'accountSummary.columnGrouping.group';
export const LOCATION_COLUMN_GROUPING_SEGMENTS = 'accountSummary.columnGrouping.segment';

// Absolute ceiling on breakdown levels per multi-hierarchy row group. The EFFECTIVE cap for a
// given report is dynamic: min(availableSegmentCount + 1 for a terminal Account Number level,
// this ceiling). This constant only bounds the dynamic cap for performance/UX safety.
export const MAX_BREAKDOWN_LEVELS_PER_ROW_GROUP = 12;

/**
 * grouping[0] is always the column header label constant.
 * grouping[1..MAX_ORGANIZE_BY_LEVELS_PER_COLUMN_GROUP] are the "organize columns by"
 * levels, outermost first. A one-level column group has exactly grouping[0..1].
 * Example (Report config)
 * "grouping": [
      { "constant": "General Ledger Balances - V4" },
      { "groupId": "24854", "segmentId": "545" },
      { "groupId": "31002", "segmentId": "601" }
    ],
 */
export const COLUMN_GROUP_CONSTANT_INDEX = 0;
/** Index of the FIRST organize-by level, not the only one. */
export const COLUMN_GROUP_ORGANIZE_BY_INDEX = 1;
/**
 * Ceiling on nested organize-by levels per column group. Columns are all materialized
 * (unlike rows, which expand lazily), so levels multiply into report width.
 */
export const MAX_ORGANIZE_BY_LEVELS_PER_COLUMN_GROUP = 3;
